/**
 * Fleur Kintsugi — brand mark for Saveurs Normandes.
 *
 * A hand-drawn fleur-de-lis fused with a molten magma drop, rendered as a
 * gold gradient SVG. Reused on: hero ornament, footer, section dividers,
 * PDF headers, wax seal center.
 *
 * Props:
 *   size?: number (px)         default 48
 *   variant?: "solid" | "line" default "solid" — "line" = only the gold hairline
 *   glow?: boolean             default true — adds warm drop-shadow
 *   className?: string         additional wrapper classes
 */
export default function FleurKintsugi({ size = 48, variant = "solid", glow = true, className = "" }) {
  const gradientId = `fleur-gold-${Math.random().toString(36).slice(2, 8)}`;
  const magmaId = `fleur-magma-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 96"
      width={size}
      height={(size * 96) / 64}
      className={className}
      style={glow ? { filter: "drop-shadow(0 0 12px rgba(245,195,112,0.55))" } : undefined}
      aria-hidden="true"
    >
      <defs>
        {/* Molten gold gradient — main body */}
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5C370" />
          <stop offset="40%" stopColor="#D99533" />
          <stop offset="70%" stopColor="#B57420" />
          <stop offset="100%" stopColor="#7A4A14" />
        </linearGradient>
        {/* Magma highlight — brighter core of the drop */}
        <radialGradient id={magmaId} cx="0.5" cy="0.45" r="0.55">
          <stop offset="0%" stopColor="#FFE29A" stopOpacity="1" />
          <stop offset="60%" stopColor="#F5C370" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#D99533" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Magma drop halo behind fleur */}
      {variant === "solid" && (
        <ellipse cx="32" cy="48" rx="18" ry="22" fill={`url(#${magmaId})`} opacity="0.5" />
      )}

      {/* Central vertical stem — the molten spine */}
      <path
        d="M32 8 C32 8, 30 20, 30 32 C30 44, 32 56, 32 68 L32 88"
        fill="none"
        stroke={variant === "line" ? "#D99533" : `url(#${gradientId})`}
        strokeWidth="1.6"
        strokeLinecap="round"
      />

      {/* Top bud — molten teardrop */}
      <path
        d="M32 6 C29 6, 27 10, 28 14 C28.5 16, 30 18, 32 18 C34 18, 35.5 16, 36 14 C37 10, 35 6, 32 6 Z"
        fill={variant === "line" ? "none" : `url(#${gradientId})`}
        stroke={variant === "line" ? "#D99533" : "none"}
        strokeWidth="1.4"
      />

      {/* Left petal — kintsugi curve */}
      <path
        d="M32 30 C24 26, 14 30, 10 40 C8 48, 14 56, 22 54 C28 52, 32 44, 32 36"
        fill={variant === "line" ? "none" : `url(#${gradientId})`}
        stroke={variant === "line" ? "#D99533" : "none"}
        strokeWidth="1.4"
        opacity={variant === "solid" ? 0.92 : 1}
      />

      {/* Right petal — mirrored */}
      <path
        d="M32 30 C40 26, 50 30, 54 40 C56 48, 50 56, 42 54 C36 52, 32 44, 32 36"
        fill={variant === "line" ? "none" : `url(#${gradientId})`}
        stroke={variant === "line" ? "#D99533" : "none"}
        strokeWidth="1.4"
        opacity={variant === "solid" ? 0.92 : 1}
      />

      {/* Horizontal band — kintsugi crossbar */}
      <path
        d="M18 44 Q32 50, 46 44"
        fill="none"
        stroke={variant === "line" ? "#D99533" : `url(#${gradientId})`}
        strokeWidth="1.8"
        strokeLinecap="round"
      />

      {/* Bottom molten drop — the magma tear */}
      <path
        d="M32 68 C28 68, 25 72, 26 78 C27 84, 32 88, 32 88 C32 88, 37 84, 38 78 C39 72, 36 68, 32 68 Z"
        fill={variant === "line" ? "none" : `url(#${gradientId})`}
        stroke={variant === "line" ? "#D99533" : "none"}
        strokeWidth="1.4"
      />

      {/* Molten spark highlights */}
      {variant === "solid" && (
        <>
          <circle cx="22" cy="42" r="1" fill="#FFE29A" opacity="0.85" />
          <circle cx="42" cy="42" r="1" fill="#FFE29A" opacity="0.85" />
          <circle cx="32" cy="12" r="0.8" fill="#FFE29A" opacity="0.9" />
        </>
      )}
    </svg>
  );
}
